import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Input,
  message,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  usePaging,
} from '@maxtropy/components';
import {
  apiV2AreaGetValveSelectPost,
  apiV2AreaList2Post,
  apiV2AreaTaskAutoControlConfirmLogPost,
  apiV2AreaTaskBatchCancelConfirmPost,
  apiV2AreaTaskGetConfirmOperatorListPost,
  apiV2AreaTaskGetConfirmStationListPost,
  V2AreaTaskAutoControlConfirmLogPostResponse,
} from '@maxtropy/device-customer-apis-v2';

const { RangePicker } = DatePicker;

interface AutoControlConfirmLogModalProps {
  open: boolean;
  onCancel: () => void;
}

type AutoControlConfirmLogItem = Exclude<V2AreaTaskAutoControlConfirmLogPostResponse['list'], undefined>[number];

export enum AutoControlStatus {
  Unconfirmed = 0,
  Confirmed = 1,
  Canceled = 2,
}

interface FilterParams {
  areaId?: number;
  valveId?: number;
  operateRange?: [Dayjs, Dayjs];
  status?: AutoControlStatus | 3;
  stationRooms?: number[];
  operatorId?: number;
  phone?: string;
}

export const statusTextMap: Record<AutoControlStatus, string> = {
  [AutoControlStatus.Confirmed]: '已确认执行',
  [AutoControlStatus.Canceled]: '已取消',
  [AutoControlStatus.Unconfirmed]: '未确认',
};

export const statusColorMap: Record<AutoControlStatus, string> = {
  [AutoControlStatus.Confirmed]: 'success',
  [AutoControlStatus.Canceled]: 'warning',
  [AutoControlStatus.Unconfirmed]: 'error',
};

export const statusOptions = [
  { label: '全部', value: 3 },
  { label: '已确认执行', value: AutoControlStatus.Confirmed },
  { label: '已取消', value: AutoControlStatus.Canceled },
  { label: '未确认', value: AutoControlStatus.Unconfirmed },
];

const baseColumns = [
  {
    title: '站房',
    dataIndex: 'scadaName',
    ellipsis: true,
    width: 120,
    render: (value?: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '供气阀门',
    dataIndex: 'valveName',
    ellipsis: true,
    width: 120,
    render: (value?: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '供气区域',
    dataIndex: 'areaName',
    ellipsis: true,
    width: 120,
    render: (value?: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '控制指令',
    dataIndex: 'commands',
    ellipsis: true,
    width: 150,
    render: (value?: string[]) => <EllipsisSpan value={value?.join('；') || '--'} />,
  },
  {
    title: '控制时间',
    dataIndex: 'executionTime',
    width: 150,
    ellipsis: true,
    render: (value?: string) => {
      const parsed = value ? dayjs(value) : undefined;
      return <EllipsisSpan value={parsed?.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '--'} />;
    },
  },
  {
    title: '操作类型',
    dataIndex: 'status',
    ellipsis: true,
    width: 110,
    render: (value?: number) => {
      if (
        value !== AutoControlStatus.Confirmed &&
        value !== AutoControlStatus.Canceled &&
        value !== AutoControlStatus.Unconfirmed
      ) {
        return '--';
      }
      return (
        <Tag border="solid" color={statusColorMap[value]}>
          {statusTextMap[value]}
        </Tag>
      );
    },
  },
  {
    title: '操作时间',
    dataIndex: 'operationTime',
    width: 150,
    ellipsis: true,
    render: (value: string, record: AutoControlConfirmLogItem) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '操作人',
    dataIndex: 'operator',
    width: 90,
    ellipsis: true,
    render: (value?: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '操作人手机号',
    dataIndex: 'cellphone',
    width: 120,
    ellipsis: true,
    render: (value?: string) => <EllipsisSpan value={value} />,
  },
  {
    title: '取消操作人',
    dataIndex: 'cancelOperator',
    width: 100,
    ellipsis: true,
    render: (value?: string) => <EllipsisSpan value={value || '--'} />,
  },
];

const AutoControlConfirmLogModal: React.FC<AutoControlConfirmLogModalProps> = ({ open, onCancel }) => {
  const [form] = Form.useForm<FilterParams>();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<FilterParams>({ status: 3 });

  const selectedAreaId = Form.useWatch('areaId', form);

  const { data: areaList } = useRequest(() => apiV2AreaList2Post().then(res => res.list ?? []), {
    ready: open,
  });
  const { data: scadaSelectData } = useRequest(
    () => apiV2AreaTaskGetConfirmStationListPost().then(res => res.list ?? []),
    {
      ready: open,
    }
  );
  const { data: operatorSelectData } = useRequest(
    () => apiV2AreaTaskGetConfirmOperatorListPost().then(res => res.list ?? []),
    {
      ready: open,
    }
  );
  const { data: valveSelectData } = useRequest(
    () => apiV2AreaGetValveSelectPost({ areaId: selectedAreaId }).then(res => res.list ?? []),
    {
      ready: open,
      refreshDeps: [selectedAreaId],
    }
  );

  const areaOptions = useMemo(
    () =>
      (areaList ?? []).filter(item => item.id !== undefined).map(item => ({ label: item.zoneName, value: item.id! })),
    [areaList]
  );

  const valveOptions = useMemo(() => {
    return (valveSelectData ?? [])?.map(item => ({ label: item.name, value: item.id! }));
  }, [valveSelectData]);

  const stationOptions = useMemo(
    () => (scadaSelectData ?? []).map(item => ({ label: item.name, value: item.id! })),
    [scadaSelectData]
  );

  const operatorOptions = useMemo(
    () =>
      (operatorSelectData ?? []).map(item => ({
        label: item.username,
        value: item.id!,
      })),
    [operatorSelectData]
  );

  const {
    data: tableData,
    loading,
    refresh,
  } = useRequest(
    async () => {
      const response = await apiV2AreaTaskAutoControlConfirmLogPost({
        areaId: searchParams.areaId,
        valveId: searchParams.valveId,
        stationIds: searchParams.stationRooms,
        operatorId: searchParams.operatorId,
        phone: searchParams.phone,
        status: searchParams.status === 3 ? undefined : searchParams.status,
        executeStartTime: searchParams.operateRange?.[0]
          ? dayjs(searchParams.operateRange[0]).startOf('D').valueOf()
          : undefined,
        executeEndTime: searchParams.operateRange?.[1]
          ? dayjs(searchParams.operateRange[1]).endOf('D').valueOf()
          : undefined,
        page: pageOffset,
        size: pageSize,
      });
      setTotalCount(response?.total ?? 0);
      return response?.list ?? [];
    },
    {
      ready: open,
      refreshDeps: [open, pageOffset, pageSize, searchParams],
    }
  );

  const resetFilters = useCallback(() => {
    form.resetFields();
    setPageOffset(1);
    const resetValues: FilterParams = { status: 3 };
    form.setFieldsValue(resetValues);
    setSearchParams(resetValues);
  }, [form, setPageOffset]);

  useEffect(() => {
    if (!open) {
      resetFilters();
    }
  }, [open, resetFilters]);

  const handleCancelExecution = (record: AutoControlConfirmLogItem) => {
    if (!record.canCancel) return;

    Modal.confirm({
      title: '取消执行确认',
      content: '确定取消该自动控制确认单吗？',
      async onOk() {
        await apiV2AreaTaskBatchCancelConfirmPost({ ids: [record.id!] });
        message.success('取消执行成功');
        refresh();
      },
    });
  };

  const columns = [
    ...baseColumns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 90,
      fixed: 'right' as const,
      render: (_: unknown, record: AutoControlConfirmLogItem) => {
        if (record.canCancel) {
          return (
            <Button type="link" onClick={() => handleCancelExecution(record)}>
              取消执行
            </Button>
          );
        }
        return (
          <Button type="link" disabled={true}>
            {record.cancelOperator ? '已取消' : '取消执行'}
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title="自动控制确认单日志"
      open={open}
      size="big"
      bodyScroll
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <CustomFilter<FilterParams>
          form={form}
          onFinish={values => {
            setPageOffset(1);
            setSearchParams(values);
          }}
          onReset={() => {
            resetFilters();
          }}
        >
          <Form.Item name="areaId" label="供气区域">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="请选择供气区域"
              options={areaOptions}
              onChange={value => {
                form.setFieldValue('valveId', undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="valveId" label="供气阀门">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="请选择供气阀门"
              options={valveOptions}
            />
          </Form.Item>
          <Form.Item name="operateRange" label="执行时间">
            <RangePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="status" label="操作类型" initialValue={3}>
            <Select showSearch allowClear optionFilterProp="label" placeholder="全部" options={statusOptions} />
          </Form.Item>
          <Form.Item name="stationRooms" label="站房">
            <Select
              mode="multiple"
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="请选择站房"
              options={stationOptions}
            />
          </Form.Item>
          <Form.Item name="operatorId" label="操作人">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="请选择操作人"
              options={operatorOptions}
            />
          </Form.Item>
          <Form.Item name="phone" label="操作手机号">
            <Input placeholder="请输入操作手机号" allowClear />
          </Form.Item>
        </CustomFilter>

        <Table
          rowKey={record => record.id ?? `${record.areaId}-${record.valveId}-${record.executionTime}`}
          columns={columns}
          dataSource={tableData ?? []}
          loading={loading}
          pagination={false}
          scroll={{ x: 1320 }}
        />
        <Paging pagingInfo={pagingInfo} />
      </div>
    </Modal>
  );
};

export default AutoControlConfirmLogModal;
